import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@arcadeai/design-system/components/ui/atoms/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@arcadeai/design-system/components/ui/atoms/tooltip";
import { cn } from "@arcadeai/design-system/lib/utils";
import type { ReactNode } from "react";
import { useState } from "react";
import type { Binding } from "@/app/types";
import { getBindingInfo } from "@/lib/binding";

type ResourceSection = {
  binding: Binding;
  itemsCount: number;
  resourceType: string;
  overriddenCount?: number;
  overriddenTooltipContent?: string;
  children?: ReactNode;
};

type HorizontalResourceViewProps = {
  sections: ResourceSection[];
  defaultActiveTab?: string;
};

type ResourceCountPillProps = {
  itemsCount: number;
  overriddenCount?: number;
};

const ResourceCountPill = ({
  itemsCount,
  overriddenCount,
}: ResourceCountPillProps) => {
  const hasOverrides = (overriddenCount ?? 0) > 0;
  if (hasOverrides) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger
            render={
              <span
                className={cn(
                  "ml-1 rounded-full px-2 py-0.5 text-xs transition-all duration-200 group-data-active:scale-105",
                  "bg-warning/50 text-warning-foreground/90 group-data-active:bg-warning group-data-active:text-warning-foreground"
                )}
              />
            }
          >
            {`${itemsCount} (${overriddenCount})`}
          </TooltipTrigger>
          <TooltipContent>
            <p>{`${overriddenCount} overridden`}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <span
      className={cn(
        "ml-1 rounded-full px-2 py-0.5 text-xs transition-all duration-200 group-data-active:scale-105",
        "bg-slate-50 font-medium group-hover:bg-slate-100 group-data-active:bg-slate-200 dark:bg-muted-foreground/18 dark:group-data-active:bg-muted-foreground/38 dark:group-hover:bg-muted-foreground/28"
      )}
    >
      {itemsCount}
    </span>
  );
};

export const HorizontalResourceView = ({
  sections,
  defaultActiveTab,
}: HorizontalResourceViewProps) => {
  const [activeTab, setActiveTab] = useState(
    defaultActiveTab || `${sections[0]?.binding}-${sections[0]?.resourceType}`
  );

  return (
    <div className="w-full">
      <Tabs
        className="w-full"
        onValueChange={(value) => {
          setActiveTab(value);
        }}
        value={activeTab}
      >
        <TabsList className="inline-flex h-11 items-center justify-start rounded-lg bg-gradient-to-b from-muted/40 to-muted/60 px-2 text-muted-foreground backdrop-blur-sm">
          {sections.map((section) => {
            const bindingInfo = getBindingInfo(section.binding);
            const { icon: Icon, classes } = bindingInfo;
            const tabValue = `${section.binding}-${section.resourceType}`;

            return (
              <TabsTrigger
                className="group relative h-8 cursor-pointer items-center gap-2 rounded-md py-1 dark:border-none"
                key={tabValue}
                value={tabValue}
              >
                <div className="relative flex size-5 items-center justify-center rounded-md data-active:scale-110">
                  <Icon
                    className={`size-3.5 ${classes.sectionIcon} transition-all duration-200 data-active:scale-110`}
                  />
                </div>
                <span className="font-medium transition-colors duration-200 group-hover:text-foreground data-active:text-accent-foreground">
                  {bindingInfo.displayName}
                </span>
                <ResourceCountPill
                  itemsCount={section.itemsCount}
                  overriddenCount={section.overriddenCount}
                />
              </TabsTrigger>
            );
          })}
        </TabsList>

        <div className="mt-1">
          {sections.map((section) => {
            const bindingInfo = getBindingInfo(section.binding);
            const { icon: Icon, classes } = bindingInfo;
            const tabValue = `${section.binding}-${section.resourceType}`;
            const emptyMessage = `No ${section.binding} ${section.resourceType} found.`;

            return (
              <TabsContent
                className="mt-0 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                key={tabValue}
                value={tabValue}
              >
                {section.children ? (
                  <div>{section.children}</div>
                ) : (
                  <div className="rounded-lg border border-dashed bg-muted/10 py-12 text-center text-muted-foreground">
                    <Icon
                      className={`mx-auto mb-4 h-12 w-12 ${classes.sectionEmptyIcon}`}
                    />
                    <h4 className="mb-2 font-medium text-lg">{emptyMessage}</h4>
                    <p className="text-muted-foreground/70 text-sm">
                      Get started by adding your first{" "}
                      {section.resourceType.toLowerCase()}.
                    </p>
                  </div>
                )}
              </TabsContent>
            );
          })}
        </div>
      </Tabs>
    </div>
  );
};
